import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type { Link } from '@repo/sdk/gen'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListToolbar } from '@repo/ui/components/list-toolbar'
import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { RefreshButton } from '@repo/ui/components/refresh-button'
import { getErrorCode } from '@repo/ui/utils'
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient
} from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { translateApp, useTranslation } from '@/lib/i18n'
import { linkPolicyQueryOptions } from '@/modules/file-manager/query/query-options'
import { GlobalSearch } from '@/modules/search/components/global-search'
import { makeQueryKeys } from '@/query/query-keys'
import { LINK_VIEW_STATES } from '@/routes/(private)/(shell)/-lib/link-views'
import { LinksEmpty } from '@/routes/(private)/(shell)/links/-components/links-empty'
import { LinksFilters } from '@/routes/(private)/(shell)/links/-components/links-filters'
import { LinksTable } from '@/routes/(private)/(shell)/links/-components/links-table'
import { linksSearchSchema } from '@/routes/(private)/(shell)/links/-lib/link-search'
import {
	linksMineQueryOptions,
	MAX_PAGE_SIZE
} from '@/routes/(private)/(shell)/links/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

export const Route = createFileRoute('/(private)/(shell)/links/')({
	component: MyLinks,
	validateSearch: linksSearchSchema,
	search: {
		middlewares: [stripSearchParams(linksSearchSchema.parse({}))]
	},
	// Outside the component, same reasoning `admin/route.tsx` gives: the
	// language comes off route context, already resolved by the session gate
	// above, rather than through the hook.
	head: ({ match }) => ({
		meta: [
			{
				title: translateApp(
					match.context.appLanguage,
					'links:page_title'
				)
			}
		]
	}),
	staticData: {
		breadcrumb: 'links:page_title'
	}
})

/**
 * "I miei link attivi" — the only place a signed-in person manages what they
 * have shared externally after creating it.
 *
 * Opens on the links still standing — `active`, and `policy_blocked` for the
 * reason `LINK_VIEW_STATES` gives — because that is what the title promises
 * and what someone opening it wants to know: what is exposed right now
 * (OPSSTD-8807). A revoked or expired link no longer reaches anybody, so it
 * stays one choice away in the state filter rather than mixed in.
 * `token` rides on every row already, so Copy needs no second call.
 *
 * A search over the linked resource's name and that one filter, unlike the
 * admin equivalent this milestone also ships: one person's own links are a
 * short enough list that most of `/admin/links`' filter set would be
 * furniture, and `GET /links` offers only `state` to filter by beyond paging.
 */
function MyLinks() {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const params = Route.useSearch()
	const pageSize = usePageSize((state) => state.pageSize)

	const viewStates = LINK_VIEW_STATES[params.view]
	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE),
		kind: params.kind === 'all' ? undefined : params.kind,
		state: viewStates && [...viewStates],
		search: params.search || undefined
	}
	const links = useInfiniteQuery(linksMineQueryOptions(listQuery))
	const rows = links.data?.links ?? EMPTY_LINKS
	const cursorExpired = getErrorCode(links.error) === 'cursor_invalid'
	const linkPolicy = useQuery(linkPolicyQueryOptions())

	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(links)
		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.links.mine(listQuery)
			})
		}
	}

	return (
		<PageLayout
			bar={
				<ListToolbar
					actions={<RefreshButton query={links} />}
					search={
						<ListInputSearch
							placeholder={t('links:search_placeholder')}
						/>
					}
				>
					<LinksFilters />
				</ListToolbar>
			}
			breadcrumb={<QueryBreadcrumbs />}
			search={<GlobalSearch />}
		>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6'>
				<LinksTable
					data={rows}
					loadMore={{
						hasMore: links.hasNextPage,
						loading: links.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					state={{
						empty: (
							<LinksEmpty
								disabledByPolicy={
									linkPolicy.data?.allowed === false
								}
								filtered={
									params.kind !== 'all' ||
									params.view !== 'live' ||
									params.search !== ''
								}
							/>
						),
						error: links.isError && !cursorExpired,
						loading: links.isPending || links.isPlaceholderData
					}}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_LINKS: Link[] = []
